import { InfoCircleOutlined } from '@ant-design/icons';
import React, { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { PortalBannerMessageFragment } from '@willow/graphql-iso/src/portal';
import { NamedMemo } from '@willow/shared-web';

import { PortalSelectedLoan } from '../../App';
import { ContactCTA } from '../contact-cta/ContactCTA';

import './Layout.scss';

export interface LayoutProps {
  loan?: PortalSelectedLoan;
  hasMultipleLoans?: boolean; // allow users with mutliple loans to switch views
  onLoanSwitch?: () => void;
  children: React.ReactNode;
  bannerMessages: PortalBannerMessageFragment[];
}

export const Layout = NamedMemo<LayoutProps>(
  'Layout',
  ({ loan, hasMultipleLoans, onLoanSwitch, children, bannerMessages }) => {
    // On loan select, update the browser history to remove the losId
    const history = useHistory();
    const { search } = useLocation();
    const handleLoanSwitch = useCallback(() => {
      // preserve impersonation when switching loans
      history.push(`/${search}`);

      if (onLoanSwitch) onLoanSwitch();
    }, [history, onLoanSwitch, search]);

    const { company, losId } = loan || {};

    return (
      <main id="main">
        <div className="wrapper">
          {bannerMessages.length > 0 && (
            <div className="p-3 u-border-1 u-border-color-red1 u-border-r-3 u-bg-white gap-2">
              {bannerMessages.map((bm, index) => (
                <div className="d-flex align-items-center gap-2" key={index}>
                  <InfoCircleOutlined className="u-color-red1" />
                  <p className="u-fs-3">{bm.message}</p>
                </div>
              ))}
            </div>
          )}

          {hasMultipleLoans && loan && (
            <section className="loan-switch-header">
              <div>Loan #{losId}</div>
              <button onClick={handleLoanSwitch}>Switch Loan</button>
            </section>
          )}

          <div className="inner">{children}</div>

          {company && (
            <div className="contact-cta-wrapper">
              <ContactCTA company={company} />
            </div>
          )}
        </div>
      </main>
    );
  },
);
